import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { router } from '@inertiajs/react'
import { Building2, Phone, Calendar, Edit, Mail, MapPin, Navigation } from 'lucide-react'
import { useAuth } from '@/components/Layout'

interface School {
  id: number
  name: string
  description: string
  phone: string
  avatar_url: string | null
  created_at: string
  address?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  latitude?: number
  longitude?: number
  checkin_radius_meters?: number
  has_location?: boolean
  full_address?: string
}

interface ProfileProps {
  school: School
}

export default function Profile({ school }: ProfileProps) {
  const { user } = useAuth()

  return (
    <DashboardLayout schoolName={school.name}>
      <div className="p-8 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Perfil da Escola</h2>
            <p className="text-muted-foreground">
              Informações sobre sua escola de surf
            </p>
          </div>
          {(user?.role === 'owner' || user?.role === 'manager') && (
            <Button onClick={() => router.visit('/school/profile/edit')}>
              <Edit className="mr-2 h-4 w-4" />
              Editar Perfil
            </Button>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {/* School Avatar Card */}
          <Card className="md:col-span-1">
            <CardHeader>
              <CardTitle>Logo da Escola</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center">
              {school.avatar_url ? (
                <img
                  src={school.avatar_url}
                  alt={school.name}
                  className="w-32 h-32 rounded-full object-cover border-4 border-primary/20"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-16 w-16 text-primary/50" />
                </div>
              )}
              <h3 className="mt-4 text-xl font-bold text-center">{school.name}</h3>
            </CardContent>
          </Card>

          {/* School Details Card */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Informações da Escola</CardTitle>
              <CardDescription>Dados cadastrados no sistema</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-muted-foreground">Nome da Escola</p>
                    <p className="text-base font-semibold">{school.name}</p>
                  </div>
                </div>

                {school.description && (
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-muted-foreground">Descrição</p>
                      <p className="text-base">{school.description}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-muted-foreground">Telefone</p>
                    <p className="text-base font-semibold">{school.phone}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-muted-foreground">Data de Cadastro</p>
                    <p className="text-base">
                      {new Date(school.created_at).toLocaleDateString('pt-BR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Location Card */}
          {(school.has_location || school.address) && (
            <Card className="md:col-span-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Localização da Escola
                </CardTitle>
                <CardDescription>
                  Endereço e coordenadas para check-in por geolocalização
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Endereço */}
                  {school.full_address && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                        Endereço
                      </h4>
                      <div className="space-y-2">
                        {school.address && (
                          <p className="text-base">{school.address}</p>
                        )}
                        <p className="text-sm text-muted-foreground">
                          {school.city && school.city}
                          {school.city && school.state && ', '}
                          {school.state && school.state}
                          {(school.city || school.state) && school.postal_code && ' - '}
                          {school.postal_code && school.postal_code}
                        </p>
                        {school.country && (
                          <p className="text-sm text-muted-foreground">{school.country}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Coordenadas */}
                  {school.has_location && school.latitude && school.longitude && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                        Coordenadas GPS
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Navigation className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">Latitude:</span>
                          <code className="text-sm bg-muted px-2 py-0.5 rounded">
                            {Number(school.latitude).toFixed(6)}
                          </code>
                        </div>
                        <div className="flex items-center gap-2">
                          <Navigation className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">Longitude:</span>
                          <code className="text-sm bg-muted px-2 py-0.5 rounded">
                            {Number(school.longitude).toFixed(6)}
                          </code>
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                          <span className="text-sm font-medium">Raio de Check-in:</span>
                          <code className="text-sm bg-primary/10 text-primary px-2 py-0.5 rounded font-semibold">
                            {school.checkin_radius_meters || 100} metros
                          </code>
                        </div>
                        <a
                          href={`https://www.google.com/maps?q=${school.latitude},${school.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm text-primary hover:underline mt-2"
                        >
                          <MapPin className="h-4 w-4" />
                          Ver no Google Maps
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Status do Check-in */}
                  <div className="md:col-span-2">
                    {school.has_location ? (
                      <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="rounded-full bg-green-100 dark:bg-green-900 p-2">
                            <MapPin className="h-5 w-5 text-green-600 dark:text-green-400" />
                          </div>
                          <div>
                            <h5 className="text-sm font-semibold text-green-900 dark:text-green-100">
                              Check-in por Geolocalização Ativo
                            </h5>
                            <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                              Alunos precisam estar dentro de {school.checkin_radius_meters || 100} metros da escola para fazer check-in pelo app mobile.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="rounded-full bg-amber-100 dark:bg-amber-900 p-2">
                            <MapPin className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div>
                            <h5 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                              Localização Não Configurada
                            </h5>
                            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                              Configure o endereço e obtenha as coordenadas para habilitar o check-in por geolocalização no app mobile.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}
